/**
 * ScreenTimeGate
 *
 * Presentational enforcement layer for screen time. Given a screen-time
 * status (typically from `useScreenTime`), it:
 *  - renders a full-screen friendly lockout overlay when the student is over
 *    their limit (or outside their allowed schedule), and
 *  - shows a dismissible break-reminder toast when a break is due.
 *
 * It renders its children unobstructed when the student is within limits.
 *
 * This component is intentionally standalone/presentational; wiring it around
 * the student app globally happens elsewhere (App.tsx).
 */

import React, { useEffect, useState } from 'react';
import type { ScreenTimeStatus } from '../../hooks/useScreenTime';
import { BADGE } from '../../config/badgeColors';

interface ScreenTimeGateProps {
  status: ScreenTimeStatus;
  children?: React.ReactNode;
  /** Optional custom break message (falls back to settings, then a default). */
  breakMessage?: string;
}

const lockoutMessageFor = (reason: ScreenTimeStatus['blockReason']): string => {
  switch (reason) {
    case 'outside-days':
      return "Not a screen-time day today. See you on your next allowed day!";
    case 'outside-hours':
      return "It's outside your allowed hours right now — come back during your screen-time window!";
    case 'weekly-limit':
      return "You've used all your time for this week! Great work 🎮 — come back next week.";
    case 'daily-limit':
    default:
      return "Time's up for today! Great work 🎮 — come back tomorrow.";
  }
};

export const ScreenTimeGate: React.FC<ScreenTimeGateProps> = React.memo(
  ({ status, children, breakMessage }) => {
    const [breakDismissed, setBreakDismissed] = useState(false);

    // Re-arm the break toast each time a new break becomes due.
    useEffect(() => {
      if (status.shouldTakeBreak) {
        setBreakDismissed(false);
      }
    }, [status.shouldTakeBreak, status.minutesUsed]);

    // Never show a lockout the server has not confirmed yet.
    //
    // 2026-09-05: this guard used to live in `ProtectedRoute`, which returned
    // a bare fragment while loading and a `<ScreenTimeGate>` afterwards. React
    // treats that as a different element type and unmounts the entire route
    // subtree, so every authenticated route was remounted a few hundred
    // milliseconds after it appeared and lost whatever the child had typed.
    // Holding the check here keeps the tree shape constant and the behaviour
    // identical.
    if (status.loading) {
      return <>{children}</>;
    }

    if (status.isOverLimit) {
      return (
        <div
          className="screen-time-lockout"
          role="alertdialog"
          aria-modal="true"
          aria-label="Screen time limit reached"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 24,
            background: 'linear-gradient(160deg, #1e3a8a 0%, #4338ca 100%)',
            color: '#ffffff',
          }}
        >
          <div style={{ fontSize: 72, lineHeight: 1, marginBottom: 16 }} aria-hidden="true">
            🎮
          </div>
          <h1 style={{ fontSize: 32, margin: '0 0 12px', fontWeight: 800 }}>
            {lockoutMessageFor(status.blockReason)}
          </h1>
          <p style={{ fontSize: 18, opacity: 0.9, maxWidth: 480 }}>
            Ask a parent if you need a little more time today.
          </p>
        </div>
      );
    }

    const showBreakToast = status.shouldTakeBreak && !breakDismissed;
    const breakText =
      breakMessage ||
      status.settings?.breakScreenMessage ||
      'Time for a quick break! Stretch, grab some water, then come back. 😊';

    return (
      <>
        {children}
        {showBreakToast && (
          <div
            className="screen-time-break-toast"
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9998,
              maxWidth: 420,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 18px',
              borderRadius: 12,
              background: '#ffffff',
              color: '#1f2937',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
          >
            <span style={{ fontSize: 24 }} aria-hidden="true">
              ☕
            </span>
            <span style={{ flex: 1, fontSize: 15 }}>{breakText}</span>
            <button
              type="button"
              onClick={() => setBreakDismissed(true)}
              aria-label="Dismiss break reminder"
              style={{
                border: 'none',
                background: BADGE.blue,
                color: '#fff',
                borderRadius: 8,
                padding: '6px 12px',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Got it
            </button>
          </div>
        )}
      </>
    );
  }
);

ScreenTimeGate.displayName = 'ScreenTimeGate';
